import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getAuditTrailByid } from '../../../../redux/auditTrailSlice'
import { useS3Data } from '../../../../hooks/useS3Data'

/* ── action → node type mapping ── */
const ACTION_TYPE_MAP = {
  SECTION_NODE_DELETED: 'section',
  STEP_NODE_DELETED: 'step',
  SUBSTEP_NODE_DELETED: 'substep',
  ATTACHMENT_NODE_DELETED: 'attachment',
  'RICH-TEXT_NODE_DELETED': 'rich-text',
  CANVAS_NODE_DELETED: 'canva',
  VIDEO_NODE_DELETED: 'video',
}

/* ── parse new_content_snapshot → versionContent + currentSections ── */
const parseNewSnapshot = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    return { versionContent: null, currentSections: [] }
  }
  const { data, ...rest } = snapshot
  return {
    versionContent: Object.keys(rest).length > 0 ? rest : null,
    currentSections: Array.isArray(data) ? data : [],
  }
}

/* ── parse previous_content_snapshot → previousSections ── */
const parsePreviousSnapshot = (snapshot) => {
  if (Array.isArray(snapshot)) return snapshot
  if (snapshot && typeof snapshot === 'object' && Array.isArray(snapshot.data)) {
    return snapshot.data
  }
  return []
}

/* ── find child (attachment/rich-text/canva/video) inside previous sections ── */
const findChildInSections = (sections, previewTreeNodeId, resolvedType, empty) => {
  let cumSteps = 0
  let globalTypeCounter = 0

  for (let sIdx = 0; sIdx < sections.length; sIdx++) {
    const sec = sections[sIdx]
    const steps = sec?.steps ?? []

    for (let stIdx = 0; stIdx < steps.length; stIdx++) {
      const step = steps[stIdx]

      // step-level children
      const stepChildren = step?.children ?? []
      const sameType = stepChildren.filter((ch) => ch?.type === resolvedType)
      for (let i = 0; i < sameType.length; i++) {
        if (sameType[i]?.tree_node_id === previewTreeNodeId) {
          const chIdx = stepChildren.findIndex((ch) => ch?.tree_node_id === previewTreeNodeId)
          return {
            ...empty,
            type: resolvedType,
            sectionIndex: sIdx,
            section: sec,
            stepIndex: stIdx,
            childIndex: chIdx,
            childNode: stepChildren[chIdx],
            globalChildNumber: globalTypeCounter + i + 1,
            globalStepStart: cumSteps,
          }
        }
      }
      globalTypeCounter += sameType.length

      // substep-level children
      const substeps = step?.substeps ?? []
      for (let subIdx = 0; subIdx < substeps.length; subIdx++) {
        const subChildren = substeps[subIdx]?.children ?? []
        const sameTypeSub = subChildren.filter((ch) => ch?.type === resolvedType)
        for (let i = 0; i < sameTypeSub.length; i++) {
          if (sameTypeSub[i]?.tree_node_id === previewTreeNodeId) {
            const subChIdx = subChildren.findIndex((ch) => ch?.tree_node_id === previewTreeNodeId)
            return {
              ...empty,
              type: resolvedType,
              sectionIndex: sIdx,
              section: sec,
              stepIndex: stIdx,
              substepIndex: subIdx,
              childIndex: subChIdx,
              childNode: subChildren[subChIdx],
              globalChildNumber: globalTypeCounter + i + 1,
              globalStepStart: cumSteps,
            }
          }
        }
        globalTypeCounter += sameTypeSub.length
      }
    }
    cumSteps += steps.length
  }
  return null
}

/* ── resolve deleted node from previousSections ── */
const resolveDeletedNode = (actionToShow, previousSections, previewTreeNodeId) => {
  const empty = {
    type: null,
    sectionIndex: -1,
    section: null,
    stepIndex: -1,
    substepIndex: -1,
    childIndex: -1,
    childNode: null,
    globalStepStart: 0,
    globalChildNumber: 0,
  }

  const nodeType = ACTION_TYPE_MAP[actionToShow]
  if (!nodeType || previousSections.length === 0 || !previewTreeNodeId) return empty

  if (nodeType === 'section') {
    const secIdx = previousSections.findIndex((s) => s?.tree_node_id === previewTreeNodeId)
    if (secIdx < 0) return empty
    const globalStepStart = previousSections
      .slice(0, secIdx)
      .reduce((sum, s) => sum + (s?.steps?.length || 0), 0)
    return { ...empty, type: 'section', sectionIndex: secIdx, section: previousSections[secIdx], globalStepStart }
  }

  if (nodeType === 'step') {
    let cumSteps = 0
    for (let sIdx = 0; sIdx < previousSections.length; sIdx++) {
      const sec = previousSections[sIdx]
      const steps = sec?.steps ?? []
      const stIdx = steps.findIndex((st) => st?.tree_node_id === previewTreeNodeId)
      if (stIdx >= 0) {
        return { ...empty, type: 'step', sectionIndex: sIdx, section: sec, stepIndex: stIdx, globalStepStart: cumSteps }
      }
      cumSteps += steps.length
    }
    return empty
  }

  if (nodeType === 'substep') {
    let cumSteps = 0
    for (let sIdx = 0; sIdx < previousSections.length; sIdx++) {
      const sec = previousSections[sIdx]
      const steps = sec?.steps ?? []
      for (let stIdx = 0; stIdx < steps.length; stIdx++) {
        const substeps = steps[stIdx]?.substeps ?? []
        const subIdx = substeps.findIndex((sub) => sub?.tree_node_id === previewTreeNodeId)
        if (subIdx >= 0) {
          return { ...empty, type: 'substep', sectionIndex: sIdx, section: sec, stepIndex: stIdx, substepIndex: subIdx, globalStepStart: cumSteps }
        }
      }
      cumSteps += steps.length
    }
    return empty
  }

  // child types: attachment, rich-text, canva, video
  return findChildInSections(previousSections, previewTreeNodeId, nodeType, empty) ?? empty
}

/* ── build checkbox highlighting state ── */
const buildCheckboxState = (resolvedType, previewTreeNodeId) => {
  const base = { auditPreviewCurrentNode: true }
  if (!resolvedType) return null

  if (resolvedType === 'section') {
    return { currentNodeId: previewTreeNodeId, currentNodeType: 'section', sourceNodeId: null, allChildrenChecked: true, ...base }
  }
  if (['step', 'substep', 'attachment', 'video', 'rich-text', 'canva'].includes(resolvedType)) {
    return { currentNodeId: previewTreeNodeId, currentNodeType: resolvedType, sourceNodeId: null, allChildrenChecked: false, ...base }
  }
  return null
}

/* ── build human-readable display title ── */
const buildDisplayTitle = (versionId, resolved) => {
  if (!versionId) return 'Loading...'
  const suffix = `(Version ${versionId}) Selected`
  if (resolved.type === 'section' && resolved.sectionIndex >= 0) {
    return `Section ${resolved.sectionIndex + 1} ${suffix}`
  }
  if (resolved.type === 'step' && resolved.stepIndex >= 0) {
    return `Step ${resolved.globalStepStart + resolved.stepIndex + 1} ${suffix}`
  }
  if (resolved.type === 'substep' && resolved.substepIndex >= 0) {
    const stepNum = resolved.globalStepStart + resolved.stepIndex + 1
    const substepLetter = String.fromCharCode(65 + resolved.substepIndex)
    return `Substep ${stepNum}${substepLetter} ${suffix}`
  }
  const childLabelMap = { attachment: 'Attachment', video: 'Video', 'rich-text': 'Rich Text', canva: 'Canvas' }
  if (childLabelMap[resolved.type] && resolved.childNode) {
    return `${childLabelMap[resolved.type]} ${resolved.globalChildNumber ? `${resolved.globalChildNumber} ` : ''}${suffix}`
  }
  return `Preview ${suffix}`
}

/* ── main hook ── */
const useDeletedNodeData = (auditTrailId) => {
  const dispatch = useDispatch()

  const [auditTrailData, setAuditTrailData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let isMounted = true
    setLoading(true)
    setError(null)

    dispatch(getAuditTrailByid({ id: auditTrailId }))
      .unwrap()
      .then((data) => {
        if (isMounted) setAuditTrailData(data?.results ?? data)
      })
      .catch((err) => {
        if (isMounted) setError(err?.message || err || 'Failed to fetch audit trail data')
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })

    return () => { isMounted = false }
  }, [dispatch, auditTrailId])

  const {
    data: newSnapshotData,
    loading: newSnapshotLoading,
    error: newSnapshotError,
  } = useS3Data(auditTrailData?.new_content_snapshot)

  const {
    data: prevSnapshotData,
    loading: prevSnapshotLoading,
    error: prevSnapshotError,
  } = useS3Data(auditTrailData?.previous_content_snapshot)

  const auditVc = auditTrailData?.version_content ?? {}
  const actionToShow = auditVc.action_to_show ?? ''
  const previewTreeNodeId = auditVc.preview_tree_node_id

  const { versionContent, currentSections } = useMemo(
    () => parseNewSnapshot(newSnapshotData),
    [newSnapshotData],
  )

  const previousSections = useMemo(
    () => parsePreviousSnapshot(prevSnapshotData),
    [prevSnapshotData],
  )

  const resolved = useMemo(
    () => resolveDeletedNode(actionToShow, previousSections, previewTreeNodeId),
    [actionToShow, previousSections, previewTreeNodeId],
  )

  const checkboxState = useMemo(
    () => buildCheckboxState(resolved.type, previewTreeNodeId),
    [resolved.type, previewTreeNodeId],
  )

  const displayTitle = useMemo(
    () => buildDisplayTitle(auditTrailData?.version_id, resolved),
    [auditTrailData?.version_id, resolved],
  )

  const snapshotLoading = newSnapshotLoading || prevSnapshotLoading
  const snapshotError = newSnapshotError || prevSnapshotError

  return {
    auditTrailData,
    loading,
    error,
    snapshotLoading,
    snapshotError,
    versionContent,
    section:previousSections,
    resolved,
    checkboxState,
    displayTitle,
    previewTreeNodeId,
    actionToShow,
    hasSnapshotUrl: !!auditTrailData?.new_content_snapshot,
    part_ref: previousSections?.[0]?.part_ref,
    procedure_rev_ref: previousSections?.[0]?.procedure_rev_ref,
    cancel_screen_data:{
      ...versionContent,
      data:currentSections
    }
  }
}

export default useDeletedNodeData
